import { LoadingDots } from '@flos/react-ui';
import { useContext } from 'react';
import { ConfigContext } from '../../../context/ConfigContext';
import { getClaimFormSubmitValue } from '../../../helpers/getClaimFormSubmitValue';
import { getElementFromElementId } from '../../../helpers/getElementFromElementId';
import { SummaryRowTemplate } from '../../../helpers/templates';
import useService from '../../../hooks/useService';
import { getEngagementServiceConfig } from '../../../services/engagements';
import useAppState from '../../../state/appState';
import { ClaimFormSubmitFieldValue } from '../../../types/claim-form-submit-field-value';
import { HouseAddress as HouseAddressType, FormConfig, FormValues } from '@topdanmark-onlinesolutions/claim-form-types';
import { IEngagementProductInsuranceDetails, IEngagements } from '../../../types/services';
import { Status } from '../../../types/status';
import { getValue } from '../../values/Get';
import { AddressBox } from './AddressBox';
import { OtherAddressDanish } from './OtherAddressDanish';
import { OtherAddressForeign } from './OtherAddressForeign';
import { OtherPlace } from './OtherPlace';
import { getDefaultAddresses } from './getDefaultAddresses';
import { isRequired } from '../../../helpers/isRequired';

export type houseAddressKeys = keyof HouseAddressType['addresses'];
export type ADDRESS_TYPE = 'CAMPING' | 'HOTEL' | 'COTTAGE' | 'HOME' | 'OTHER';

export type POLICIES_NAME =
    | 'husforsikring'
    | 'hus'
    | 'fritidshus'
    | 'fritidshus bygning'
    | 'fritidshus u/opførelse'
    | 'indbo'
    | 'indboforsikring'
    | 'indboforsikring til unge'
    | 'fritidshusforsikring';

export type AddressOption = {
    valueOnSubmit: valueOnSubmit;
    // these properties are for the selectableBox options in `AddressBox.tsx`
    label: string;
    value: houseAddressKeys | IEngagementProductInsuranceDetails['description'];
};
type valueOnSubmit = {
    label: string;
    buttonType: ADDRESS_TYPE;
    policies?: AddressOptionPolicies[];
    description?: string;
    // CA needed these for sewer and building
    policyNumber?: string;
    address?: string;
    type?: string;
};

type AddressOptionPolicies = {
    number: string;
    type: POLICIES_NAME;
};

const HouseAddress = ({ caption, elementId, addresses }: HouseAddressType) => {
    const formValues = useAppState((state) => state.formValues);
    const formConfig = useAppState((state) => state.formConfig);
    const setFormValue: (key: string, value: AddressOption) => void = useAppState((state) => state.setFormValue);
    const appConfig = useContext(ConfigContext);

    const { value } = HouseAddress.getValueOrDefault(formValues, elementId);

    const isRequiredField = isRequired(formConfig, elementId);

    const { data: engagements, status } = useService<IEngagements>(getEngagementServiceConfig(appConfig));
    if (status === Status.LOADING) {
        return <LoadingDots centered={false} />;
    }

    return (
        <div>
            {caption && <h6>{caption}</h6>}

            <AddressBox
                elementId={elementId}
                addresses={addresses}
                engagements={engagements}
                setFormValue={setFormValue}
                formSubmitApiPath={formConfig?.submitApiPath}
            />

            {value === 'showOtherAddressDanish' && (
                <OtherAddressDanish elementId={elementId} setFormValue={setFormValue} isRequired={isRequiredField} />
            )}

            {value === 'showOtherAddressForeign' && (
                <OtherAddressForeign elementId={elementId} setFormValue={setFormValue} isRequired={isRequiredField} />
            )}

            {value === 'showOtherPlace' && (
                <OtherPlace elementId={elementId} setFormValue={setFormValue} isRequired={isRequiredField} />
            )}
        </div>
    );
};

HouseAddress.isValid = (formValues: FormValues, elementId: string) => {
    const valueObject: AddressOption | undefined = HouseAddress.getValue(formValues, elementId);

    if (valueObject) {
        switch (valueObject.value) {
            case 'showOtherAddressDanish':
            case 'showOtherAddressForeign':
            case 'showOtherPlace':
                const { description } = valueObject.valueOnSubmit;
                if (description && description.length > 0) {
                    return true;
                } else {
                    return false;
                }
            case 'showInMyVehicle':
            case 'showInAHotelOrOtherCottage':
            case `showPolicyAddresses-${valueObject.label}`:
                return true;
            default:
                return false;
        }
    } else {
        return false;
    }
};

HouseAddress.getValue = (formValues: FormValues, elementId: string) => {
    return formValues[elementId];
};

HouseAddress.ResumePage = (formConfig: FormConfig, formValues: FormValues, elementId: string) => {
    const element = getElementFromElementId(formConfig, elementId) as HouseAddressType;
    const { summaryCaption } = element;
    const valueObject = getValue(formConfig, formValues, elementId);

    if (!(valueObject === undefined)) {
        switch (valueObject.value) {
            case 'showOtherAddressDanish':
            case 'showOtherAddressForeign':
            case 'showOtherPlace':
                const { description } = valueObject.valueOnSubmit;
                if (description && description.length > 0) {
                    return <SummaryRowTemplate elementId={elementId} caption={summaryCaption} value={description} />;
                } else {
                    return <></>;
                }
            case 'showInMyVehicle':
            case 'showInAHotelOrOtherCottage':
            case `showPolicyAddresses-${valueObject.label}`:
                return (
                    <SummaryRowTemplate
                        elementId={elementId}
                        caption={summaryCaption}
                        value={valueObject.valueOnSubmit.label}
                    />
                );
            default:
                return <></>;
        }
    } else {
        return <></>;
    }
};

HouseAddress.getValueSubmit = (
    formValues: FormValues,
    elementId: string,
    caption: string,
    formConfig?: FormConfig
): ClaimFormSubmitFieldValue => {
    const valueObj = HouseAddress.getValue(formValues, elementId);
    const valueStr = HouseAddress.getValueHuman(formValues, elementId);
    const policyNumber = valueObj.valueOnSubmit.policies?.[0]?.number;

    // CA needs different formats for these two claim forms; otherwise, it will fail. When CA converts XML to JSON, this can be removed
    if (formConfig?.submitApiPath === 'kloak-privat' || formConfig?.submitApiPath === 'doere-og-vinduer-privat') {
        const valueId = {
            policyNumber: policyNumber ? policyNumber : '',
            address: policyNumber ? valueObj.valueOnSubmit.label : valueObj.valueOnSubmit.description,
            type: valueObj.valueOnSubmit.buttonType
        };

        return getClaimFormSubmitValue('addressradioselection', elementId, caption, valueStr, valueId);
    }

    const valueId = {
        ...valueObj.valueOnSubmit
    };

    // component is called `addressradioselection` in CA.
    return getClaimFormSubmitValue('addressradioselection', elementId, caption, valueStr, valueId);
};

HouseAddress.getValueHuman = (formValues: FormValues, elementId: string) => {
    const valueObject: AddressOption = HouseAddress.getValue(formValues, elementId);

    if (valueObject) {
        switch (valueObject.value) {
            case 'showOtherAddressDanish':
            case 'showOtherAddressForeign':
            case 'showOtherPlace':
                const { description } = valueObject.valueOnSubmit;
                if (description && description.length > 0) {
                    return description;
                } else {
                    return '';
                }
            case 'showInMyVehicle':
            case 'showInAHotelOrOtherCottage':
            case `showPolicyAddresses-${valueObject.label}`:
                return valueObject.valueOnSubmit.label;
            default:
                return '';
        }
    } else {
        return '';
    }
};

HouseAddress.getValueOrDefault = (formValues: FormValues, elementId: string) => {
    const value: AddressOption | undefined = HouseAddress.getValue(formValues, elementId);

    if (value === undefined) {
        return {
            ...getDefaultAddresses('showOtherPlace'),
            value: ''
        } as AddressOption;
    }

    return value;
};

HouseAddress.isFieldValid = (fieldHasHadFocusAtLeastOnce: boolean, formValues: FormValues, elementId: string) => {
    if (!fieldHasHadFocusAtLeastOnce) return undefined;

    return HouseAddress.isValid(formValues, elementId);
};

export default HouseAddress;
